import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";
import { Icon, RangeNumberInput, RangeNumberInputProps } from "@evergis/ui";
import { useDebouncedCallback } from "@evergis/react";

import { StyledIconButton } from "./styled";
import { FILTER_UPDATE_DEBOUNCE } from "./constants";
import { getConfigFilter } from "../../utils/getConfigFilter";
import { getDate } from "../../../../utils/date";
import { getDataSource } from "../../utils/getDataSource";
import { useWidgetContext } from "../../../Dashboard/hooks/useWidgetContext";
import { WidgetFilterProps } from "./types";

export const RangeDateFilter: FC<WidgetFilterProps> = ({ type, filter, config }) => {
  const { t } = useTranslation("featureCard");
  const { changeFilters, dataSources } = useWidgetContext(type);
  const [value, setValue] = useState([undefined, undefined] as [Date, Date]);
  const isInit = useRef(false);
  const updateDebounce = useDebouncedCallback(FILTER_UPDATE_DEBOUNCE);

  const { filters: configFilters } = config;
  const { filterName, label, minValue, maxValue, withTime } = filter.options;

  const configFilter = useMemo(() => getConfigFilter(filterName, configFilters), [configFilters, filterName]);
  const dataSource = useMemo(
    () => getDataSource(configFilter?.relatedDataSource, dataSources),
    [configFilter?.relatedDataSource, dataSources],
  );
  const { minFromData, maxFromData } = useMemo(
    () =>
      dataSource.features.reduce(
        (prev, { attributes }) => {
          const date = getDate(attributes[configFilter?.attributeValue]);

          return {
            minFromData: !prev.minFromData ? date : date < prev.minFromData ? date : prev.minFromData,
            maxFromData: !prev.maxFromData ? date : date > prev.maxFromData ? date : prev.maxFromData,
          };
        },
        { minFromData: null, maxFromData: null },
      ),
    [configFilter?.attributeValue, dataSource.features],
  );

  const minDate = useMemo(() => getDate(minValue) || minFromData, [minFromData, minValue]);
  const maxDate = useMemo(() => getDate(maxValue) || maxFromData, [maxFromData, maxValue]);

  const isEmpty = useMemo(() => isNil(value?.[0]) && isNil(value?.[1]), [value]);
  const isDefault = useMemo(
    () => value?.[0]?.toString() === minDate?.toString() && value?.[1]?.toString() === maxDate?.toString(),
    [maxDate, minDate, value],
  );

  const reset = useCallback(() => {
    setValue([minDate, maxDate] as [Date, Date]);
    changeFilters(
      {
        [filterName]: {
          value: [minDate, maxDate],
          min: minDate,
          max: maxDate,
        },
      },
      configFilter?.resetFilters,
    );
  }, [changeFilters, configFilter?.resetFilters, filterName, maxDate, minDate]);

  useEffect(() => {
    if (!isNil(minDate) && !isNil(maxDate)) {
      reset();
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isInit.current) {
      isInit.current = true;

      return;
    }

    updateDebounce(() => {
      changeFilters(
        {
          [filterName]: {
            value,
            min: value[0],
            max: value[1],
          },
        },
        configFilter?.resetFilters,
      );
    });
  }, [value]); // eslint-disable-line

  if (!configFilter) return null;

  return (
    <RangeNumberInput
      label={label || t("interval")}
      selectedOption={{ range: true }}
      minDate={minDate}
      maxDate={maxDate}
      value={value}
      zIndex={100}
      prefix={<Icon kind="date" disabled />}
      suffix={!isEmpty && !isDefault && <StyledIconButton kind="error" onClick={reset} />}
      withTime={withTime}
      isDate
      onChange={setValue as RangeNumberInputProps<Date>["onChange"]}
    />
  );
};
